import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { FiGrid, FiUser, FiSettings, FiFileText, FiChevronDown, FiMenu as FiList } from "react-icons/fi";
import AdminTopBar from "./AdminTopBar";
import { authFetch, fetchSiteIdentity } from "../api";
import defaultLogo from "../assets/logo.png";

const settingsLinks = [
  { label: "Site Identity", to: "/admin/settings/site-identity" },
  { label: "Navbar", to: "/admin/settings/navbar" },
  { label: "Footer", to: "/admin/settings/footer" },
];

// Pages that have their own sub-menu in the admin sidebar, keyed by the page's menu slug.
// Any other page just links to its generic "banner & body" editor.
const pageSections = {
  home: [
    { label: "Browser Title", to: "/admin/pages/home" },
    { label: "Home Banner", to: "/admin/pages/home/hero-carousel" },
    { label: "Services", to: "/admin/pages/home/services" },
    { label: "Sub Services", to: "/admin/pages/home/sub-services" },
    { label: "Systems", to: "/admin/pages/home/systems" },
  ],
  "ai-tools": [
    { label: "Categories", to: "/admin/pages/ai-tools/categories" },
  ],
  "co-work": [
    { label: "Browser Title & Logo", to: "/admin/pages/co-work" },
    { label: "Banner Slides", to: "/admin/pages/co-work/slides" },
    { label: "Marketplace Content", to: "/admin/pages/co-work/content" },
    { label: "Jobs", to: "/admin/pages/co-work/jobs" },
    { label: "Members", to: "/admin/pages/co-work/members" },
  ],
  foundation: [
    { label: "Banner & Body", to: "/admin/pages/foundation" },
    { label: "Help Requests", to: "/admin/pages/foundation/requests" },
    { label: "Help Offers", to: "/admin/pages/foundation/offers" },
    { label: "Foundation Content", to: "/admin/pages/foundation/content" },
  ],
  blogs: [
    { label: "Blog Posts", to: "/admin/pages/blogs/posts" },
    { label: "Blogs Banner & Body", to: "/admin/pages/blogs" },
    { label: "IT Services Blogs", to: "/admin/pages/blogs-it-services" },
    { label: "AI Tools Blogs", to: "/admin/pages/blogs-ai-tools" },
    { label: "CoWork Marketplace Blogs", to: "/admin/pages/blogs-cowork-marketplace" },
    { label: "Foundation Blogs", to: "/admin/pages/blogs-foundation" },
  ],
  contact: [
    { label: "Banner & Body", to: "/admin/pages/contact" },
    { label: "Messages", to: "/admin/pages/contact/messages" },
  ],
  portfolio: [
    { label: "Banner & Body", to: "/admin/pages/portfolio" },
    { label: "Projects", to: "/admin/pages/portfolio/projects" },
  ],
};

function navLinkClass({ isActive }) {
  return `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
    isActive ? "bg-brand-gradient text-white" : "text-gray-300 hover:bg-white/5 hover:text-white"
  }`;
}

export default function AdminLayout() {
  const location = useLocation();
  const [settingsOpen, setSettingsOpen] = useState(location.pathname.startsWith("/admin/settings"));
  const [pagesOpen, setPagesOpen] = useState(location.pathname.startsWith("/admin/pages"));
  const [openGroups, setOpenGroups] = useState(() =>
    Object.fromEntries(Object.keys(pageSections).map((slug) => [slug, location.pathname.startsWith(`/admin/pages/${slug}`)]))
  );
  const toggleGroup = (slug) => setOpenGroups((prev) => ({ ...prev, [slug]: !prev[slug] }));
  const [pages, setPages] = useState([]);
  const [logoUrl, setLogoUrl] = useState(defaultLogo);

  useEffect(() => {
    authFetch("/core/menu/")
      .then((res) => res.json())
      .then((items) =>
        setPages(items.filter((i) => !i.parent).sort((a, b) => a.order - b.order))
      )
      .catch(() => {});
    fetchSiteIdentity().then((identity) => { if (identity.logo) setLogoUrl(identity.logo); }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen flex bg-navy-darker text-white">
      <aside className="w-64 shrink-0 bg-navy border-r border-navy-border flex flex-col">
        <div className="h-16 shrink-0 flex items-center px-6 border-b border-navy-border">
          <img src={logoUrl} alt="Mr. Aali Online Technologies" className="h-8" />
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          <NavLink to="/admin/dashboard" className={navLinkClass}>
            <FiGrid /> Dashboard
          </NavLink>
          <NavLink to="/admin/profile" className={navLinkClass}>
            <FiUser /> Profile
          </NavLink>

          <button
            onClick={() => setPagesOpen((v) => !v)}
            className="w-full flex items-center justify-between gap-3 px-4 py-2.5 rounded-lg text-sm font-medium text-gray-300 hover:bg-white/5 hover:text-white transition-colors"
          >
            <span className="flex items-center gap-3"><FiFileText /> Pages</span>
            <FiChevronDown className={`transition-transform ${pagesOpen ? "rotate-180" : ""}`} />
          </button>
          {pagesOpen && (
            <div className="ml-4 pl-4 border-l border-navy-border space-y-1">
              {pages.map((page) => {
                const sections = pageSections[page.slug];
                if (sections) {
                  const open = !!openGroups[page.slug];
                  return (
                    <div key={page.id}>
                      <button
                        onClick={() => toggleGroup(page.slug)}
                        className="w-full flex items-center justify-between gap-3 px-4 py-2 rounded-lg text-sm font-medium text-gray-300 hover:bg-white/5 hover:text-white transition-colors"
                      >
                        <span className="flex items-center gap-3"><FiList size={14} /> {page.title}</span>
                        <FiChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
                      </button>
                      {open && (
                        <div className="ml-4 pl-4 border-l border-navy-border space-y-1">
                          {sections.map((section) => (
                            <NavLink key={section.to} to={section.to} end className={navLinkClass}>
                              {section.label}
                            </NavLink>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }
                return (
                  <NavLink key={page.id} to={`/admin/pages/${page.slug}`} className={navLinkClass}>
                    <FiList size={14} /> {page.title}
                  </NavLink>
                );
              })}
            </div>
          )}

          <button
            onClick={() => setSettingsOpen((v) => !v)}
            className="w-full flex items-center justify-between gap-3 px-4 py-2.5 rounded-lg text-sm font-medium text-gray-300 hover:bg-white/5 hover:text-white transition-colors"
          >
            <span className="flex items-center gap-3"><FiSettings /> Site Settings</span>
            <FiChevronDown className={`transition-transform ${settingsOpen ? "rotate-180" : ""}`} />
          </button>
          {settingsOpen && (
            <div className="ml-4 pl-4 border-l border-navy-border space-y-1">
              {settingsLinks.map((link) => (
                <NavLink key={link.to} to={link.to} className={navLinkClass}>
                  <FiList size={14} /> {link.label}
                </NavLink>
              ))}
            </div>
          )}
        </nav>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <AdminTopBar />
        <main className="flex-1 p-8 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
